import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import Cookies from 'js-cookie';
import config from '../config';
import { decrypt, encrypt } from '../utils/encryption';
import api from '../services/api';
import UserService from '../services/user';

interface AuthorityListItem {
  authority_id?: number
  authority_name?: string
  authority_feature: string
  comment?: string
}

interface Authority {
  authority_group_id?: number
  authority_group_name?: string
  comment?: string
  authorityList: AuthorityListItem[]
}

interface ResponseUser {
  data: {
    user: User
    token: string
    user_roles: UserRoles
    permissions: string[],
    authority: Authority[]
  }
}

interface LoginUser {
  id: string,
  password: string,
  remember: boolean
}

interface User {
  id: number
  name: string
  mail: string
  user_roles: string
  administrator_certification: boolean
}

export enum UserRole {
  MANAGER = 'manager',
  USER = 'user',
}

export type UserRoles = UserRole.MANAGER | UserRole.USER | null;

// TODO: seperate into UsersContext and AuthContext
interface UserContextInterface {
  isAuthenticated: boolean,
  user: Partial<User>,
  users: User[],
  getUsers: (page:number, searchText: string) => Promise<void>,
  loginUser: (data: LoginUser) => void,
  logoutUser: () => void,
  validationErrors: string[],
  loading: boolean,
  errorMessage: string,
  authority: Authority[]
  userCan: (feature: string) => boolean
  userRoles: UserRoles
}

const UserContext = createContext<UserContextInterface>({} as UserContextInterface);
UserContext.displayName = 'UserContext';

interface Props {
  children: JSX.Element
}

interface UserIdentity { user: User; authorities: Authority[]; token: string; userRoles: UserRoles }

interface UserContextCookieValue {
  token: string
  user: {
    name: string
  }
  authorities: Authority[]
  userRoles: UserRoles
}

export const UserProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useState<Partial<User>>();
  const [users, setUsers] = useState<User[]>([]);
  const [authorities, setAuthorities] = useState<Authority[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState<UserRoles>(null);

  const userCan = useCallback((feature: string) => {
    if (authorities == null) {
      return false;
    }

    return authorities.some((item) => item.authorityList.some((authority) => authority.authority_feature === feature));
  }, [authorities]);

  const getUsers = useCallback((page: number) => UserService.get(page).then((users) => {
    setUsers(users.data.data.map((u) => ({
      id: u.id,
      name: u.name,
      mail: u.mail,
      user_roles: u.user_roles,
      administrator_certification: u.administrator_certification ?? false,
    })));
  }), []);

  useEffect(() => {
    const { cookieName } = config.auth;
    const cookiesList = Cookies.get();

    const decryptedValue = cookiesList[cookieName];

    if (decryptedValue == null) {
      return;
    }

    if (decryptedValue === '') {
      return;
    }

    const decrypted = decrypt(decryptedValue);

    let userIdentity: UserIdentity;
    try {
      userIdentity = JSON.parse(decrypted) as UserIdentity;
    } catch (e) {
      console.error(e);
      return;
    }

    if (userIdentity == null) {
      setIsAuthenticated(false);
      Cookies.remove(cookieName);
      return;
    }

    setIsAuthenticated(true);
    const authUser = userIdentity.user;
    setUser(authUser);
    setAuthorities(userIdentity.authorities);
    setUserRoles(userIdentity.userRoles);
  }, []);

  const loginUser = useCallback(({ id, password, remember }: LoginUser) => {
    setLoading(true);
    setValidationErrors([]);
    setErrorMessage('');
    api.post<User, ResponseUser>('/auth/admin-login', {
      mail: id,
      password,
    }).then((response) => {
      const {
        token, user,
      } = response.data;

      if (response.data.user_roles !== UserRole.MANAGER) {
        setErrorMessage('権限はありません');
        return;
      }

      const authorityLogin:Authority = {
        authorityList: [{
          authority_feature: 'user_logged',
        }],
      };

      const authorities = [authorityLogin];
      const { cookieName } = config.auth;
      const cookieRawValue:UserContextCookieValue = {
        token, user, authorities, userRoles,
      };
      const cookieValue = encrypt(JSON.stringify(cookieRawValue));
      Cookies.set(cookieName, cookieValue, {
        secure: config.env === 'production',
        expires: remember ? 365 : null, // if the user didn't select remember me, cookie expires on session end
      });
      setIsAuthenticated(true);
      location.href = '/';
    }).catch((error) => {
      // TODO: サーバー側のレスポンスと擦り合わせる
      if (error?.response?.data?.error) {
        setValidationErrors(error.response.data.error);
        return;
      }
      if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message);
        return;
      }

      setErrorMessage('some error');
    }).finally(() => {
      setLoading(false);
    });
  }, [userRoles]);

  const logoutUser = useCallback(() => {
    setIsAuthenticated(false);
    api.post<User, ResponseUser>('/auth/logout', {}).then(() => {
      Cookies.remove(config.auth.cookieName);
      location.href = '/login';
    });
  }, []);

  const value = useMemo(
    () => ({
      userCan,
      authority: authorities,
      isAuthenticated,
      user,
      getUsers,
      users,
      loginUser,
      logoutUser,
      validationErrors,
      loading,
      errorMessage,
      userRoles,
    }),
    [isAuthenticated, errorMessage, authorities, loading, user, users, userCan, loginUser, logoutUser, getUsers, validationErrors, userRoles],
  );

  return (
    <UserContext.Provider
      value={value}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextInterface => {
  const {
    userCan,
    authority,
    user,
    users,
    getUsers,
    logoutUser,
    loginUser,
    isAuthenticated,
    validationErrors,
    loading,
    errorMessage,
    userRoles,
  } = useContext(UserContext);

  return {
    userCan,
    authority,
    isAuthenticated,
    user,
    users,
    getUsers,
    loginUser,
    logoutUser,
    validationErrors,
    loading,
    errorMessage,
    userRoles,
  };
};
